import { useCallback, useEffect, useMemo } from 'react'
import AudioManager from '@/lib/audio/AudioManager'
import { useSetState } from 'ahooks'

interface AudioState {
  isPlaying: boolean
  isLoading: boolean
  currentTime: number
  duration: number
}

export default function useAudioPlayer(src: string) {
  const audioManager = useMemo(() => AudioManager.getInstance(), [])

  const initialState =
    audioManager.getState().currentSrc === src
      ? {
          isPlaying: true,
          isLoading: false,
          currentTime: audioManager.getState().currentTime,
          duration: audioManager.getState().duration,
        }
      : {
          isPlaying: false,
          isLoading: false,
          currentTime: 0,
          duration: 0,
        }

  const [audioState, setAudioState] = useSetState<AudioState>(initialState)

  // Lister for events related to this audio
  useEffect(() => {
    const unsubscribe = audioManager.subscribe((newState) => {
      if (newState.currentSrc === src) {
        setAudioState({
          isPlaying: newState.isPlaying,
          isLoading: newState.isLoading,
          currentTime: newState.currentTime,
          duration: newState.duration,
        })
      } else if (audioState.isPlaying) {
        setAudioState({
          isPlaying: false,
          isLoading: false,
          currentTime: 0,
          duration: 0,
        })
      }
    })

    return () => {
      unsubscribe()
    }
  }, [audioManager, audioState.isPlaying, setAudioState, src])

  const play = useCallback(() => {
    audioManager.play(src)
  }, [audioManager, src])

  const stop = useCallback(() => {
    if (audioManager.getState().currentSrc !== src) {
      return
    }
    audioManager.stop()
  }, [audioManager, src])

  const seek = useCallback(
    (time: number) => {
      if (audioManager.getState().currentSrc !== src) {
        return
      }
      audioManager.seek(time)
    },
    [audioManager, src]
  )

  return {
    audioManager,
    isPlaying: audioState.isPlaying,
    currentTime: audioState.currentTime,
    duration: audioState.duration,
    play,
    stop,
    seek,
  }
}
